import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, amberDark, useTheme } from '../tokens';
import FixedAssetsView from './FixedAssetsView';

const teal = '#0D9488';

// Shared helpers
function fmtAmt(val) {
  if (val == null) return '--';
  const neg = val < 0;
  const s = '$' + Math.abs(val).toLocaleString('en-CA', { minimumFractionDigits: 0 });
  return neg ? `(${s})` : s;
}

function SectionHeader({ label, t }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 0.5, color: teal, fontFamily: sans,
      padding: '10px 0 4px',
      borderBottom: `2px solid ${teal}20`,
      marginBottom: 6,
    }}>
      {label}
    </div>
  );
}

function GridHeader({ columns, t }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: columns.map(c => c.width || '1fr').join(' '),
      padding: '4px 0', gap: 8, borderBottom: `2px solid ${t.border}`, marginBottom: 4,
    }}>
      {columns.map((col, i) => (
        <span key={i} style={{
          fontSize: 8, fontWeight: 700, color: t.textFaint,
          textTransform: 'uppercase', textAlign: col.align || 'left',
        }}>{col.label}</span>
      ))}
    </div>
  );
}

function GridRow({ values, columns, t, bold }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: columns.map(c => c.width || '1fr').join(' '),
      padding: '5px 0', gap: 8, alignItems: 'center',
      borderBottom: bold ? `2px solid ${t.border}` : `1px solid ${t.borderLight || t.border}`,
    }}>
      {values.map((val, i) => (
        <span key={i} style={{
          fontSize: 11, fontFamily: columns[i].mono ? mono : sans,
          fontWeight: bold ? 700 : (columns[i].bold ? 600 : 400),
          color: typeof val === 'number' && val < 0 ? red : (bold ? t.textPrimary : t.textSecondary),
          textAlign: columns[i].align || 'left',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {columns[i].format ? columns[i].format(val) : val}
        </span>
      ))}
    </div>
  );
}

function EmptyWorkpaper({ label, panelType, t }) {
  return (
    <div style={{
      textAlign: 'center', padding: '32px 20px',
      color: t.textFaint, fontSize: 12,
    }}>
      <div style={{ fontSize: 14, marginBottom: 8, color: t.textMuted }}>
        {label} Workpaper
      </div>
      <div style={{ marginBottom: 12 }}>
        Import data or connect your ERP to populate this {panelType.replace(/_/g, ' ')}.
      </div>
      <div style={{
        marginTop: 12, padding: '16px', borderRadius: 8,
        border: `2px dashed ${t.border}`,
        fontSize: 11, color: t.textFaint,
      }}>
        Drop supporting files here or click to upload
      </div>
    </div>
  );
}


// ─── Rollforward Schedule ───
function RollforwardView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Opening', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Additions', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Disposals', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Closing', width: '90px', align: 'right', mono: true, bold: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="rollforward" t={t} />;

  // Mock rollforward from TB data
  const rows = accounts.map(a => ({
    code: a.code,
    name: a.name,
    opening: Math.round((a.balance || 0) * 0.92),
    additions: Math.round(Math.abs(a.balance || 0) * 0.12),
    disposals: Math.round(Math.abs(a.balance || 0) * -0.04),
    closing: a.balance || 0,
  }));

  const totals = rows.reduce((acc, r) => ({
    opening: acc.opening + r.opening,
    additions: acc.additions + r.additions,
    disposals: acc.disposals + r.disposals,
    closing: acc.closing + r.closing,
  }), { opening: 0, additions: 0, disposals: 0, closing: 0 });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Rollforward`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.opening, r.additions, r.disposals, r.closing]} columns={cols} t={t} />
      ))}
      <GridRow values={['', 'Total', totals.opening, totals.additions, totals.disposals, totals.closing]} columns={cols} t={t} bold />
      <RecDiff label="Per GL" glBalance={totals.closing} wpBalance={totals.closing} t={t} />
    </div>
  );
}

// ─── Depreciation / Amortization Schedule ───
function DepreciationView({ panel, accounts, t }) {
  const isAmort = panel.panelType === 'amortization_schedule';
  const termLabel = isAmort ? 'Amortization' : 'Depreciation';
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Cost', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: `Accum ${termLabel.slice(0, 5)}.`, width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Net Book Value', width: '90px', align: 'right', mono: true, bold: true, format: fmtAmt },
    { label: `Monthly ${termLabel.slice(0, 5)}.`, width: '90px', align: 'right', mono: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType={panel.panelType} t={t} />;

  const rows = accounts.map(a => {
    const bal = Math.abs(a.balance || 0);
    const cost = Math.round(bal * 1.8);
    const accum = cost - bal;
    return {
      code: a.code,
      name: a.name,
      cost,
      accum: -accum,
      nbv: bal,
      monthly: -Math.round(accum / 36),
    };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- ${termLabel} Schedule`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.cost, r.accum, r.nbv, r.monthly]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Aging Schedule (AR/AP) ───
function AgingView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Current', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: '31-60', width: '70px', align: 'right', mono: true, format: fmtAmt },
    { label: '61-90', width: '70px', align: 'right', mono: true, format: fmtAmt },
    { label: '90+', width: '70px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Total', width: '80px', align: 'right', mono: true, bold: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="aging_schedule" t={t} />;

  const rows = accounts.map(a => {
    const total = a.balance || 0;
    const current = Math.round(total * 0.65);
    const d31 = Math.round(total * 0.20);
    const d61 = Math.round(total * 0.10);
    const d90 = total - current - d31 - d61;
    return { code: a.code, name: a.name, current, d31, d61, d90, total };
  });

  const totals = rows.reduce((acc, r) => ({
    current: acc.current + r.current,
    d31: acc.d31 + r.d31,
    d61: acc.d61 + r.d61,
    d90: acc.d90 + r.d90,
    total: acc.total + r.total,
  }), { current: 0, d31: 0, d61: 0, d90: 0, total: 0 });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Aging Analysis`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.current, r.d31, r.d61, r.d90, r.total]} columns={cols} t={t} />
      ))}
      <GridRow values={['', 'Total', totals.current, totals.d31, totals.d61, totals.d90, totals.total]} columns={cols} t={t} bold />
      <RecDiff label="Per GL" glBalance={totals.total} wpBalance={totals.total} t={t} />
    </div>
  );
}

// ─── Debt Schedule ───
function DebtScheduleView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Opening', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Drawdowns', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Repayments', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Closing', width: '80px', align: 'right', mono: true, bold: true, format: fmtAmt },
    { label: 'Interest', width: '70px', align: 'right', mono: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="debt_schedule" t={t} />;

  const rows = accounts.map(a => {
    const closing = a.balance || 0;
    const opening = Math.round(closing * 1.03);
    const repay = opening - closing;
    return {
      code: a.code, name: a.name,
      opening, drawdowns: 0, repayments: -repay, closing,
      interest: -Math.round(Math.abs(closing) * 0.005),
    };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Debt Schedule`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.opening, r.drawdowns, r.repayments, r.closing, r.interest]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Payroll Reconciliation ───
function PayrollRecView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Per Payroll', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Per GL', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Difference', width: '80px', align: 'right', mono: true, bold: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="payroll_reconciliation" t={t} />;

  const rows = accounts.map(a => {
    const gl = a.balance || 0;
    const payroll = gl; // Reconciled = 0 diff
    return { code: a.code, name: a.name, payroll, gl, diff: gl - payroll };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Payroll Reconciliation`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.payroll, r.gl, r.diff]} columns={cols} t={t} />
      ))}
      <div style={{
        marginTop: 10, padding: '8px 12px', borderRadius: 6,
        background: greenLight + '30', border: `1px solid ${green}30`,
        fontSize: 11, color: greenDark, fontWeight: 500,
      }}>
        Payroll provider report must be uploaded to complete this reconciliation.
      </div>
    </div>
  );
}

// ─── Tax Reconciliation ───
function TaxRecView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Per Return/Filing', width: '100px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Per GL', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Difference', width: '80px', align: 'right', mono: true, bold: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="tax_reconciliation" t={t} />;

  const rows = accounts.map(a => {
    const gl = a.balance || 0;
    return { code: a.code, name: a.name, filing: gl, gl, diff: 0 };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Tax Reconciliation`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.filing, r.gl, r.diff]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Lease Schedule ───
function LeaseScheduleView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'ROU Asset', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Lease Liability', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Monthly Payment', width: '90px', align: 'right', mono: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="lease_schedule" t={t} />;

  const rows = accounts.map(a => {
    const bal = Math.abs(a.balance || 0);
    return {
      code: a.code, name: a.name,
      rou: bal, liability: -bal, payment: -Math.round(bal / 48),
    };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Lease Schedule (IFRS 16)`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.rou, r.liability, r.payment]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Equity Rollforward ───
function EquityRollforwardView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Opening', width: '90px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Issuances', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Net Income', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Closing', width: '90px', align: 'right', mono: true, bold: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="equity_rollforward" t={t} />;

  const rows = accounts.map(a => {
    const closing = a.balance || 0;
    const opening = Math.round(closing * 0.95);
    return {
      code: a.code, name: a.name,
      opening, issuances: 0, netIncome: closing - opening, closing,
    };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Equity Rollforward`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.opening, r.issuances, r.netIncome, r.closing]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Variance Analysis (P&L) ───
function VarianceView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Actual', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Budget', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Variance $', width: '80px', align: 'right', mono: true, bold: true, format: fmtAmt },
    { label: 'Var %', width: '60px', align: 'right', mono: true },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="variance_analysis" t={t} />;

  const rows = accounts.map(a => {
    const actual = a.balance || 0;
    const budget = Math.round(actual * (0.9 + Math.random() * 0.2));
    const varAmt = actual - budget;
    const varPct = budget !== 0 ? Math.round((varAmt / Math.abs(budget)) * 100) : 0;
    return { code: a.code, name: a.name, actual, budget, varAmt, varPct: varPct + '%' };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Budget vs Actual`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.actual, r.budget, r.varAmt, r.varPct]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Investment Schedule ───
function InvestmentView({ panel, accounts, t }) {
  const cols = [
    { label: 'Account', width: '70px', mono: true },
    { label: 'Description', width: '1fr' },
    { label: 'Cost', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Accrued Int.', width: '80px', align: 'right', mono: true, format: fmtAmt },
    { label: 'Fair Value', width: '90px', align: 'right', mono: true, bold: true, format: fmtAmt },
  ];

  if (accounts.length === 0) return <EmptyWorkpaper label={panel.label} panelType="investment_schedule" t={t} />;

  const rows = accounts.map(a => {
    const fv = a.balance || 0;
    const cost = Math.round(fv * 0.97);
    return {
      code: a.code, name: a.name,
      cost, interest: fv - cost, fv,
    };
  });

  return (
    <div>
      <SectionHeader label={`${panel.label} -- Investment Schedule`} t={t} />
      <GridHeader columns={cols} t={t} />
      {rows.map((r, i) => (
        <GridRow key={i} values={[r.code, r.name, r.cost, r.interest, r.fv]} columns={cols} t={t} />
      ))}
    </div>
  );
}

// ─── Reconciliation Diff Footer ───
function RecDiff({ label, glBalance, wpBalance, t }) {
  const diff = glBalance - wpBalance;
  const reconciled = diff === 0;
  return (
    <div style={{
      marginTop: 12, padding: '10px 14px', borderRadius: 8,
      background: reconciled ? greenLight + '30' : redLight + '30',
      border: `1px solid ${reconciled ? green + '30' : red + '30'}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: reconciled ? greenDark : red }}>
        {reconciled ? 'Reconciled -- no difference' : `Unreconciled difference: ${fmtAmt(diff)}`}
      </span>
      <span style={{ fontSize: 10, fontFamily: mono, color: t.textFaint }}>
        {label}: {fmtAmt(glBalance)}
      </span>
    </div>
  );
}


// ─── Main dispatcher ───
export default function PanelWorkpaperView({ panel, glMappings, selectedMonth }) {
  const t = useTheme();

  const accounts = useMemo(() => {
    if (!glMappings || !glMappings.accounts || !panel) return [];
    return glMappings.accounts.filter(a => a.panelId === panel.id);
  }, [glMappings, panel]);

  const type = panel?.panelType;

  // PP&E — use the dedicated Fixed Assets view (Supabase-backed register + 6 sections)
  if (panel?.id === 'bs-nca-ppe') {
    return <FixedAssetsView panel={panel} selectedMonth={selectedMonth} />;
  }

  switch (type) {
    case 'rollforward':
      return <RollforwardView panel={panel} accounts={accounts} t={t} />;
    case 'depreciation_schedule':
    case 'amortization_schedule':
      return <DepreciationView panel={panel} accounts={accounts} t={t} />;
    case 'aging_schedule':
      return <AgingView panel={panel} accounts={accounts} t={t} />;
    case 'debt_schedule':
      return <DebtScheduleView panel={panel} accounts={accounts} t={t} />;
    case 'payroll_reconciliation':
      return <PayrollRecView panel={panel} accounts={accounts} t={t} />;
    case 'tax_reconciliation':
      return <TaxRecView panel={panel} accounts={accounts} t={t} />;
    case 'lease_schedule':
      return <LeaseScheduleView panel={panel} accounts={accounts} t={t} />;
    case 'equity_rollforward':
      return <EquityRollforwardView panel={panel} accounts={accounts} t={t} />;
    case 'variance_analysis':
      return <VarianceView panel={panel} accounts={accounts} t={t} />;
    case 'investment_schedule':
      return <InvestmentView panel={panel} accounts={accounts} t={t} />;
    // bank_reconciliation is handled by BankRecPanel separately
    // journal_entry, checklist, generic, fx_revaluation, intercompany, etc. use default
    default:
      return <EmptyWorkpaper label={panel?.label || 'Panel'} panelType={type || 'generic'} t={t} />;
  }
}
